/**
 * SECTOR COMPLETE % — the compass ring fill (Compass Sector Fill spec, 18 Oct 2026 §1–2, §4).
 *
 * "How much of what I've taken on in this sector is FINISHED?"
 *   POOL     = every leaf task of every ACTIVE project in the sector
 *              + every leaf KYRS task in the sector (Joel, 18 Oct: "count tasks as well")
 *   Complete = DONE / TOTAL over that pool (null when TOTAL = 0 → "—", no fill)
 *
 * A project's tasks are its TKNE Directions (designs.basic_stages): a step with
 * sub-steps counts its sub-steps, never itself (leaf tasks only, no double
 * counting); a step without sub-steps counts once. ACTIVE = in the loaded
 * (is_active) set, not complete, with at least one leaf. COMPLETE = every leaf
 * done — it leaves the pool and is logged under "Finished this season".
 * KYRS tasks: listed, leaf only (a task with subtasks counts its subtasks),
 * sector = tasks.sector, else its list's olmps_sector (the list's default
 * sector). Recurring METIS instances never count here (they count in Alignment).
 *
 * Home, the compass and the sector overview all call computeComplete — the
 * one source, so the three always match. Pure — no DB/React/clock.
 */
import type { Sector } from '../domain/olmps';
import type { OracleProject } from './useOracleProjects';

export interface ProjectLeaf {
  id: string;
  done: boolean;
  /** yyyy-MM-dd set in TKNE, if any. */
  dueDate: string | null;
}

/** A project's leaf tasks: sub-steps where a step has them, else the step itself. */
export function projectLeaves(p: Pick<OracleProject, 'steps'>): ProjectLeaf[] {
  const out: ProjectLeaf[] = [];
  for (const s of p.steps) {
    if (s.substeps.length) for (const sub of s.substeps) out.push({ id: sub.id, done: sub.checked, dueDate: sub.dueDate });
    else out.push({ id: s.id, done: s.status === 'C', dueDate: s.dueDate });
  }
  return out;
}

/** COMPLETE = has leaves and every one is done. A 0-task project is never complete (it is "no tasks yet"). */
export function isProjectComplete(p: Pick<OracleProject, 'steps'>): boolean {
  const leaves = projectLeaves(p);
  return leaves.length > 0 && leaves.every((l) => l.done);
}

/** A KYRS leaf task already resolved to its sector (see useKyrsTasks). */
export interface KyrsLeafTask {
  id: string;
  sector: Sector;
  done: boolean;
}

export interface SectorComplete {
  sector: Sector;
  /** 0..1, null = "—" (nothing in the pool). */
  complete: number | null;
  done: number;
  total: number;
  /** Of which KYRS tasks (the rest are project tasks). */
  taskDone: number;
  taskTotal: number;
  /** Projects in the pool. */
  active: OracleProject[];
  /** Complete projects finished this season (out of the pool). */
  finished: OracleProject[];
  /** Projects with no tasks yet (never in the pool). */
  empty: OracleProject[];
}

const MS_DAY = 24 * 3600 * 1000;

export function computeComplete(
  sector: Sector,
  projects: Partial<Record<Sector, OracleProject[]>>,
  opts: { now?: Date; seasonDays?: number; tasks?: KyrsLeafTask[] } = {},
): SectorComplete {
  const list = projects[sector] ?? [];
  const since = opts.now && opts.seasonDays ? opts.now.getTime() - opts.seasonDays * MS_DAY : null;
  const active: OracleProject[] = [];
  const finished: OracleProject[] = [];
  const empty: OracleProject[] = [];
  let done = 0;
  let total = 0;
  for (const p of list) {
    const leaves = projectLeaves(p);
    if (!leaves.length) {
      empty.push(p);
      continue;
    }
    if (leaves.every((l) => l.done)) {
      // Logged when it finished; with no timestamp we can't place it, so it stays listed.
      const at = p.updatedAt ? Date.parse(p.updatedAt) : NaN;
      if (since == null || !Number.isFinite(at) || at >= since) finished.push(p);
      continue;
    }
    active.push(p);
    total += leaves.length;
    done += leaves.filter((l) => l.done).length;
  }
  const mine = (opts.tasks ?? []).filter((t) => t.sector === sector);
  const taskDone = mine.filter((t) => t.done).length;
  done += taskDone;
  total += mine.length;
  return { sector, complete: total ? done / total : null, done, total, taskDone, taskTotal: mine.length, active, finished, empty };
}

/** Whole-number display: "50% complete" / "—". */
export const completeLabel = (c: number | null) => (c == null ? '—' : `${Math.round(c * 100)}%`);

/** §4 ring bands, k = 1 (outermost) … 5 (innermost): band_k = clamp((C − 0.2(k−1)) / 0.2, 0, 1). */
export function ringBands(complete: number | null): number[] {
  const c = complete ?? 0;
  return [0, 1, 2, 3, 4].map((i) => Math.max(0, Math.min(1, (c - 0.2 * i) / 0.2)));
}
